// src/lib/modelConfig.js
//
// Single source of truth for which Claude model drives which surface
// of the landing-v2 experience:
//   • Chat (multi-turn conversation)        → Sonnet
//   • Episode document generation           → Sonnet
//
// Chat moved from Haiku → Sonnet (Drew's call, superseding the original
// audit decision). The chat system prompt is large, nuanced, and full
// of rules Haiku followed only loosely (marching the question list,
// folding on pushback, robotic voice). Sonnet follows it. The cost /
// latency hit that originally pinned chat to Haiku is absorbed by
// prompt-caching the static system block (see chatTurn.js) — the
// huge per-mode prompt is identical every turn, so after the first
// call it's a ~0.1× cache read instead of full input.
//
// Designed so future "version tiers" (Suno-style v1, v1.5, v2) can be
// swapped in by changing only this file. The /api/claude endpoint
// already accepts model strings; we pass these constants through and
// the server resolves to the real Anthropic model ID.
//
// Keep the keys here in sync with the resolver in api/claude.js if you
// rename them.

export const MODEL_CONFIG = {
  // Conversational chat — Sonnet. Every chat turn calls Claude; the
  // static system prompt is prompt-cached so the recurring cost is the
  // cache-read rate, not full Sonnet input pricing.
  chat: 'sonnet',

  // Full episode document — slower, smarter. Single call per
  // generation, so latency is acceptable up to ~6s (covered by the
  // 3-line scanner loading sequence on the landing).
  generation: 'sonnet',

  // Memory extraction (background — pulling structured fields from
  // conversation log). Cheap because it's processing a transcript
  // and emitting JSON. Same as chat.
  memoryExtraction: 'haiku',
};

// ─── Generation tiers — the user-facing model selector ────────────────────
// Suno-style version labels: surface a version number, never the underlying
// model name. The `model` field is the alias /api/claude.js resolves; the
// `creditAction` keys into CREDIT_COSTS so each tier carries its own price
// and the selector can show the cost difference explicitly.
export const GENERATION_TIERS = [
  {
    id:           'draft',
    model:        'haiku',
    label:        'v4.5',
    tagline:      'Fastest — a solid first pass',
    credits:      3,
    creditAction: 'episode_build_draft',
  },
  {
    id:           'standard',
    model:        'sonnet',
    label:        'v4.6',
    tagline:      'Best for most episodes',
    credits:      6,
    creditAction: 'episode_build_simple',
  },
  {
    id:           'premium',
    model:        'opus',
    label:        'v4.8',
    tagline:      'Max quality — our smartest model',
    credits:      10,
    creditAction: 'episode_build_premium',
  },
];

const TIER_STORAGE_KEY = 'epio_generation_tier';
const DEFAULT_TIER = 'standard';

export function getGenerationTier() {
  try {
    const t = localStorage.getItem(TIER_STORAGE_KEY);
    return GENERATION_TIERS.some(x => x.id === t) ? t : DEFAULT_TIER;
  } catch {
    return DEFAULT_TIER;
  }
}

export function setGenerationTier(tierId) {
  const valid = GENERATION_TIERS.some(x => x.id === tierId) ? tierId : DEFAULT_TIER;
  try { localStorage.setItem(TIER_STORAGE_KEY, valid); } catch {}
  return valid;
}

export function tierInfo(tierId = getGenerationTier()) {
  return GENERATION_TIERS.find(x => x.id === tierId) || GENERATION_TIERS[1];
}

// The credit action the current tier's episode build should charge.
export function episodeBuildCreditAction() {
  return tierInfo().creditAction;
}

// Helper for components that need to know which model to call. The
// generation surface resolves through the user's selected tier; chat and
// memory extraction stay pinned (conversation quality / cost are tuned
// independently of the build).
export function modelFor(surface) {
  if (surface === 'generation') return tierInfo().model;
  return MODEL_CONFIG[surface] || MODEL_CONFIG.chat;
}
